import {
  activitiesConfigToViewModel,
  ImplementActivitiesFormViewModel,
  viewModelToActivitiesConfig,
} from "portal/components/atk/ImplementActivitiesFormViewModel";
import {
  Autotractor,
  AutotractorSettings,
  HWV2InputPinConfiguration,
  HWV2RelayConfiguration,
  Implement,
  IOLinkConfig,
  IOLinkGenericSensor,
  IOLinkRegisterDevice,
  IOLinkScvPositionSensor,
  JobSettings,
  ScvConnection,
  ScvConnections,
  ScvInput,
  ScvOutput,
  ThreePointControls,
  Tractor,
} from "protos/atk/autotractor";
import { ControlScheme, DataModelVersion } from "protos/atk/enums";
import { createContext } from "react";
import { Duration } from "protos/google/protobuf/duration";
import {
  emptyStringToUndefined,
  setAndTouch,
  undefinedToEmptyString,
} from "portal/utils/forms";
import { FormikProps } from "formik";
import {
  ImplementConfiguration,
  TractorConfiguration,
} from "portal/utils/atkEquipment";
import { JobType } from "protos/atk/job_type";
import { Replace } from "common/utils/types";
import { typedPath } from "typed-path";

const S = typedPath<RigDetailsFormState>();

const EIGHT_HOURS_SECONDS = 28_800;

export interface RigDetailsFormState {
  dataModelVersion: DataModelVersion;
  id: string;
  name: string;
  serial: string;
  customerId: string;
  customerUuid: string;
  tractor: Tractor | undefined;
  jobSettings: Replace<
    JobSettings,
    {
      checklistTimeoutS: number; // we convert to/from protobuf durations
    }
  >;
  implements: Implement[];
  scvConnectionsByInput: { [scvInputId: string]: ScvConnection };
  atkConfig: {
    // optional enums are a pain to handle with formik widgets; use a string
    // that maps to the enum instead
    atkSettings: Replace<
      AutotractorSettings,
      {
        controlSchemeOverride: "none" | "wheeled" | "tracked";
      }
    >;
    ioLink: {
      present: boolean;
      config: Replace<
        IOLinkConfig,
        {
          readRegisters: (Replace<
            IOLinkRegisterDevice,
            {
              generic: Replace<IOLinkGenericSensor, { name: string }>;
              scvPosition: Replace<
                IOLinkScvPositionSensor,
                {
                  scvConnectionId: string;
                }
              >;
            }
          > & {
            sensorType: "scv-position" | "generic";
          })[];
        }
      >;
    };
    relays: {
      mode: "hw20";
      hw20: HWV2RelayConfiguration;
    };
    gpios: {
      mode: "hw20";
      hw20: HWV2InputPinConfiguration;
    };
    activities: ImplementActivitiesFormViewModel;
    threePointAutonomy: {
      enable: boolean;
      maxLiftTimeMs: number;
      maxLowerTimeMs: number;
      controls: Replace<ThreePointControls, { toplinkScvOutputId: string }>;
    };
    scvAutonomy: {
      heightScvConnectionId: string;
      aux1ScvConnectionId: string;
      aux2ScvConnectionId: string;
    };
  };
}

/**
 * there's a bunch of computed values which make it considerably easier to do
 * some of the cross-referencing this form needs to have a decent UX.  compute
 * them once at the toplevel and pass them back down to save a bunch of prop
 * drilling and/or local memos.
 */
export interface RigDetailsComputedFormState {
  rig: Autotractor;
  resolvedTractorConfig: TractorConfiguration | undefined;
  resolvedImplementConfigs: ImplementConfiguration[];
  scvOutputsById: { [scvOutputId: string]: ScvOutput };
  scvInputsById: { [scvInputId: string]: ScvInput };
  scvConnectionSelectOptions: {
    connectionId: string;
    implement: Implement;
    scvInput: ScvInput;
    positionFeedback: boolean;
  }[];
}

export const RigDetailsComputedFormState =
  createContext<RigDetailsComputedFormState>({
    rig: {
      dataModelVersion: DataModelVersion.DATA_MODEL_VERSION_UNSPECIFIED,
      id: "",
      customerProvidedName: "",
      serial: "",
      settings: undefined,
      customerId: "",
      customerUuid: "",
      jobSettings: {
        jobType: JobType.JOB_TYPE_UNSPECIFIED,
        checklistDefinitionId: 0,
        checklistTimeoutS: { seconds: EIGHT_HOURS_SECONDS, nanos: 0 },
      },
      implements: [],
      controls: undefined,
      scvConnections: undefined,
    },
    resolvedTractorConfig: undefined,
    resolvedImplementConfigs: [],
    scvOutputsById: {},
    scvInputsById: {},
    scvConnectionSelectOptions: [],
  });

function mapScvConnections(connections: ScvConnections | undefined): {
  [scvInputId: string]: ScvConnection;
} {
  const map: { [scvInputId: string]: ScvConnection } = {};

  for (const connection of connections?.connections ?? []) {
    map[connection.implementScvInputId] = connection;
  }

  return map;
}

function loadIoLinkConfig(
  cfg: IOLinkConfig | undefined
): RigDetailsFormState["atkConfig"]["ioLink"] {
  if (cfg === undefined) {
    return {
      present: false,
      config: {
        ipAddress: "10.12.18.1",
        modbusPort: 502,
        readRegisters: [],
      },
    };
  } else {
    return {
      present: true,
      config: {
        ...cfg,
        readRegisters: cfg.readRegisters.map((reg) => ({
          ...reg,
          sensorType:
            reg.scvPosition === undefined ? "generic" : "scv-position",
          generic: {
            name: undefinedToEmptyString(reg.generic?.name),
          },
          scvPosition: {
            scvConnectionId: undefinedToEmptyString(
              reg.scvPosition?.scvConnectionId
            ),
          },
        })),
      },
    };
  }
}

function saveIoLinkConfig(
  formState: RigDetailsFormState
): IOLinkConfig | undefined {
  if (formState.atkConfig.ioLink.present) {
    return {
      ipAddress: formState.atkConfig.ioLink.config.ipAddress,
      modbusPort: formState.atkConfig.ioLink.config.modbusPort,
      readRegisters: formState.atkConfig.ioLink.config.readRegisters.map(
        (reg) => {
          switch (reg.sensorType) {
            case "generic": {
              return {
                name: "",
                id: reg.id,
                address: reg.address,
                deviceId: reg.deviceId,
                conversion: reg.conversion,
                generic: {
                  name: reg.generic.name,
                },
              };
            }
            case "scv-position": {
              return {
                name: "",
                id: reg.id,
                address: reg.address,
                deviceId: reg.deviceId,
                conversion: reg.conversion,
                scvPosition: {
                  scvConnectionId: reg.scvPosition.scvConnectionId,
                },
              };
            }
          }
        }
      ),
    };
  } else {
    return undefined;
  }
}

/**
 * this disaster area of a function cleans up SCV connection IDs which have
 * been rendered broken references, but only if they match a list of affected
 * connection IDs.
 *
 * @param formik formik props to update the form state with
 * @param path the path to update, as provided by typed-path's `$rawPath`
 *   method
 * @param ifScvConnectionIdsIn the set of SCV connection IDs to delete
 */
function clearAndTouchSelectedScvConnectionIds(
  formik: FormikProps<RigDetailsFormState>,
  path: (string | number | symbol)[],
  ifScvConnectionIdsIn: string[]
): void {
  // okay, walk the form state object using the path until we get to the thing
  // that was indexed.
  let ptr: any = formik.values;
  let rest = [...path];

  while (rest.length > 0) {
    if (ptr === undefined) {
      // undefined isn't in the set of connection IDs so don't do anything
      return;
    }

    ptr = ptr[rest[0]!];
    rest = rest.slice(1);
  }

  // now we've walked the path, see if what it pointed to is in the array
  // of connection IDs and if so, clear the field
  if (ifScvConnectionIdsIn.includes(ptr)) {
    const strPath = path.join(".");
    formik.setFieldValue(strPath, "");
    formik.setFieldTouched(strPath);
  }
}

/**
 * removing a tractor from the rig can invalidate all sorts of references
 * elsewhere in the form state.  clean those up.
 */
export function removeTractor(formik: FormikProps<RigDetailsFormState>): void {
  // deleting the tractor invalidates all SCV connections, which is convenient
  setAndTouch(formik, S.scvConnectionsByInput, {});
  setAndTouch(formik, S.atkConfig.scvAutonomy.heightScvConnectionId, "");
  setAndTouch(formik, S.atkConfig.scvAutonomy.aux1ScvConnectionId, "");
  setAndTouch(formik, S.atkConfig.scvAutonomy.aux2ScvConnectionId, "");

  for (
    let i = 0;
    i < formik.values.atkConfig.ioLink.config.readRegisters.length;
    i++
  ) {
    setAndTouch(
      formik,
      S.atkConfig.ioLink.config.readRegisters[i]!.scvPosition.scvConnectionId,
      ""
    );
  }

  // it also invalidates the toplink configuration if any
  setAndTouch(
    formik,
    S.atkConfig.threePointAutonomy.controls.toplinkScvOutputId,
    ""
  );

  setAndTouch<Tractor | undefined, RigDetailsFormState>(
    formik,
    S.tractor,
    undefined
  );
}

/**
 * removing an implement from the rig can invalidate all sorts of references
 * elsewhere in the form state.  clean those up.
 */
export function removeImplement(
  formik: FormikProps<RigDetailsFormState>,
  computedState: RigDetailsComputedFormState,
  implementId: string
): void {
  const affectedConnectionIds: string[] = [];

  for (const implement of computedState.resolvedImplementConfigs) {
    if (implement.id === implementId) {
      // clean up the connections to all of this implement's SCV inputs
      for (const input of implement.scvInputs?.inputs ?? []) {
        const connection = formik.values.scvConnectionsByInput[input.id];

        if (connection) {
          affectedConnectionIds.push(connection.id);
        }

        setAndTouch<ScvConnection | undefined, RigDetailsFormState>(
          formik,
          S.scvConnectionsByInput[input.id]!,
          undefined
        );
      }

      break;
    }
  }

  // now, clean up SCV connection IDs only if they match one of the ones
  // associated with this implement
  clearAndTouchSelectedScvConnectionIds(
    formik,
    S.atkConfig.scvAutonomy.heightScvConnectionId.$rawPath,
    affectedConnectionIds
  );
  clearAndTouchSelectedScvConnectionIds(
    formik,
    S.atkConfig.scvAutonomy.aux1ScvConnectionId.$rawPath,
    affectedConnectionIds
  );
  clearAndTouchSelectedScvConnectionIds(
    formik,
    S.atkConfig.scvAutonomy.aux2ScvConnectionId.$rawPath,
    affectedConnectionIds
  );

  for (
    let regIndex = 0;
    regIndex < formik.values.atkConfig.ioLink.config.readRegisters.length;
    regIndex++
  ) {
    clearAndTouchSelectedScvConnectionIds(
      formik,
      S.atkConfig.ioLink.config.readRegisters[regIndex]!.scvPosition
        .scvConnectionId.$rawPath,
      affectedConnectionIds
    );
  }

  // and actually delete the implement
  const otherImplements = formik.values.implements.filter(
    (imp) => imp.id !== implementId
  );
  setAndTouch(formik, S.implements, otherImplements);
}

export function loadFormStateFromRig(
  rig: Autotractor | undefined,
  serial: string | undefined,
  customerId: string | undefined,
  customerUuid: string | undefined,
  defaultChecklistDefinitionId: number = 0
): RigDetailsFormState {
  const state: RigDetailsFormState = {
    dataModelVersion:
      rig?.dataModelVersion ?? DataModelVersion.DATA_MODEL_VERSION_UNSPECIFIED,
    id: rig?.id ?? "",
    name: rig?.customerProvidedName ?? "",
    serial: rig?.serial ?? serial ?? "",
    customerId: rig?.customerId ?? customerId ?? "-1",
    customerUuid: rig?.customerUuid ?? customerUuid ?? "",
    tractor: rig?.tractor ?? undefined,
    implements: rig?.implements ?? [],
    jobSettings: {
      // rigs with no job type set default to ground prep
      jobType: rig?.jobSettings?.jobType || JobType.GROUND_PREP,
      checklistTimeoutS:
        rig?.jobSettings?.checklistTimeoutS?.seconds ?? EIGHT_HOURS_SECONDS,
      checklistDefinitionId:
        rig?.jobSettings?.checklistDefinitionId || defaultChecklistDefinitionId,
    },
    scvConnectionsByInput: mapScvConnections(rig?.scvConnections),
    atkConfig: {
      atkSettings: {
        maxSpeedMph: rig?.settings?.maxSpeedMph ?? 10,
        maxSpeedSafetyBypassedMph:
          rig?.settings?.maxSpeedSafetyBypassedMph ?? 1.5,
        differentialBrakingEnabled:
          rig?.settings?.differentialBrakingEnabled ?? false,
        maxSteeringAngleImplementEngaged:
          rig?.settings?.maxSteeringAngleImplementEngaged ?? 5,
        hitchDownVal: rig?.settings?.hitchDownVal ?? 20,
        controlSchemeOverride: "none", // overwritten later
      },
      ioLink: loadIoLinkConfig(rig?.controls?.ioLinkCfg),
      relays: {
        mode: "hw20",
        hw20: rig?.controls?.ioConfig?.relayConfig?.hwV2Cfg ?? {
          estopConnected: false,
          momentaryDisableConnected: false,
          momentaryEnableConnected: false,
        },
      },
      gpios: {
        mode: "hw20",
        hw20: rig?.controls?.ioConfig?.inputConfig?.hwV2Cfg ?? {},
      },
      threePointAutonomy: {
        enable: rig?.controls?.liftLowerControls?.threePoint !== undefined,
        maxLiftTimeMs:
          rig?.controls?.liftLowerControls?.maxLiftTimeMs ?? 10_000,
        maxLowerTimeMs:
          rig?.controls?.liftLowerControls?.maxLowerTimeMs ?? 10_000,
        controls: {
          minRaisedValue:
            rig?.controls?.liftLowerControls?.threePoint?.minRaisedValue ?? 80,
          maxLoweredValue:
            rig?.controls?.liftLowerControls?.threePoint?.maxLoweredValue ?? 20,
          toplinkScvOutputId: undefinedToEmptyString(
            rig?.controls?.liftLowerControls?.threePoint?.toplinkScvOutputId
          ),
        },
      },
      scvAutonomy: {
        heightScvConnectionId: undefinedToEmptyString(
          rig?.controls?.scvAutonomy?.heightScvConnectionId
        ),
        aux1ScvConnectionId: undefinedToEmptyString(
          rig?.controls?.scvAutonomy?.aux1ScvConnectionId
        ),
        aux2ScvConnectionId: undefinedToEmptyString(
          rig?.controls?.scvAutonomy?.aux2ScvConnectionId
        ),
      },
      activities: activitiesConfigToViewModel(
        rig?.controls?.scvAutonomy?.activities
      ),
    },
  };

  switch (rig?.settings?.controlSchemeOverride) {
    case ControlScheme.CONTROL_SCHEME_TRACKED: {
      state.atkConfig.atkSettings.controlSchemeOverride = "tracked";
      break;
    }
    case ControlScheme.CONTROL_SCHEME_WHEELED: {
      state.atkConfig.atkSettings.controlSchemeOverride = "wheeled";
      break;
    }
  }

  return state;
}

export function saveFormStateToRig(
  formState: RigDetailsFormState
): Autotractor {
  const controls: Autotractor["controls"] = {
    liftLowerControls: {
      maxLiftTimeMs: formState.atkConfig.threePointAutonomy.maxLiftTimeMs,
      maxLowerTimeMs: formState.atkConfig.threePointAutonomy.maxLowerTimeMs,
      threePoint: formState.atkConfig.threePointAutonomy.enable
        ? {
            minRaisedValue:
              formState.atkConfig.threePointAutonomy.controls.minRaisedValue,
            maxLoweredValue:
              formState.atkConfig.threePointAutonomy.controls.maxLoweredValue,
            toplinkScvOutputId: emptyStringToUndefined(
              formState.atkConfig.threePointAutonomy.controls.toplinkScvOutputId
            ),
          }
        : undefined,
    },
    ioConfig: {
      relayConfig: {
        hwV2Cfg:
          // necessary while there's only one config mode
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          formState.atkConfig.relays.mode === "hw20"
            ? formState.atkConfig.relays.hw20
            : undefined,
      },
      inputConfig: {
        hwV2Cfg:
          // necessary while there's only one config mode
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          formState.atkConfig.gpios.mode === "hw20"
            ? formState.atkConfig.gpios.hw20
            : undefined,
      },
    },
    ioLinkCfg: saveIoLinkConfig(formState),
    scvAutonomy: {
      heightScvConnectionId: emptyStringToUndefined(
        formState.atkConfig.scvAutonomy.heightScvConnectionId
      ),
      aux1ScvConnectionId: emptyStringToUndefined(
        formState.atkConfig.scvAutonomy.aux1ScvConnectionId
      ),
      aux2ScvConnectionId: emptyStringToUndefined(
        formState.atkConfig.scvAutonomy.aux2ScvConnectionId
      ),
      activities: viewModelToActivitiesConfig(formState.atkConfig.activities),
    },
    activities: {
      activities: [],
    },
  };

  let controlSchemeOverride: ControlScheme | undefined;

  if (formState.atkConfig.atkSettings.controlSchemeOverride === "tracked") {
    controlSchemeOverride = ControlScheme.CONTROL_SCHEME_TRACKED;
  } else if (
    formState.atkConfig.atkSettings.controlSchemeOverride === "wheeled"
  ) {
    controlSchemeOverride = ControlScheme.CONTROL_SCHEME_WHEELED;
  }
  const checklistTimeoutS: Duration = {
    seconds: formState.jobSettings.checklistTimeoutS,
    nanos: 0,
  };

  return {
    id: formState.id,
    dataModelVersion: formState.dataModelVersion,
    serial: formState.serial,
    customerId: formState.customerId,
    customerUuid: formState.customerUuid,
    customerProvidedName: formState.name,
    jobSettings: {
      ...formState.jobSettings,
      jobType: formState.jobSettings.jobType
        ? Number(formState.jobSettings.jobType) // we need to explicitly send a number otherwise these get parsed as a string and end up malformed
        : Number(JobType.JOB_TYPE_UNSPECIFIED),
      checklistTimeoutS,
    },
    tractor: formState.tractor,
    implements: formState.implements,
    scvConnections: {
      connections: Object.values(formState.scvConnectionsByInput),
    },
    controls,
    settings: {
      ...formState.atkConfig.atkSettings,
      controlSchemeOverride,
    },
  };
}

export function editingSupported(formState: RigDetailsFormState): boolean {
  return (
    formState.dataModelVersion < DataModelVersion.DATA_MODEL_VERSION_M6_BASE
  );
}
